/**
 * AICoachModal - Modal for interacting with the AI Training Coach
 *
 * Allows users to:
 * - Send natural language requests to adjust their training plan
 * - Review proposed changes with original vs updated comparison
 * - Accept, decline, or ask for revision of changes
 */

import {
  AutoAwesome as AIIcon,
  Check as AcceptIcon,
  Close as CloseIcon,
  ExpandLess,
  ExpandMore,
  Refresh as RevisionIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNotification } from '../../contexts/NotificationContext';
import type { AgentResponse, AgentSessionChange } from '../../services/agentService';
import apiClient from '../../services/api';
import { logger } from '../../utils/logger';
import { getAllWorkouts } from '../../utils/planUtils';
import { LoadingAnimation } from '../common/LoadingAnimation';

interface AICoachModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChangesApplied: () => void;
  /** Optional initial prompt to auto-send when modal opens */
  initialPrompt?: string;
}

/**
 * Format intensity for display
 */
const formatIntensity = (intensity: string): string => {
  return intensity.charAt(0).toUpperCase() + intensity.slice(1);
};

/**
 * Format duration in minutes to readable string
 */
const formatDuration = (minutes: number): string => {
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
};

/**
 * Single change item component with expandable details
 */
const ChangeItem: React.FC<{
  change: AgentSessionChange;
  expanded: boolean;
  onToggle: () => void;
}> = ({ change, expanded, onToggle }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={1}
      sx={{
        mb: 1,
        overflow: 'hidden',
        borderLeft: `4px solid ${theme.palette.primary.main}`,
      }}
    >
      <ListItem
        onClick={onToggle}
        sx={{
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        secondaryAction={
          <IconButton edge="end" aria-label={expanded ? 'Collapse details' : 'Expand details'}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        }
      >
        <ListItemText
          primary={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                {change.dayOfWeek}, Week {change.weekNumber}
              </Typography>
              <Chip
                label={change.original.sport}
                size="small"
                color={change.original.sport === 'bike' ? 'primary' : 'secondary'}
                sx={{ textTransform: 'capitalize' }}
              />
            </Box>
          }
          secondary={
            <Typography variant="body2" color="text.secondary" noWrap>
              {change.reason}
            </Typography>
          }
        />
      </ListItem>

      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          {/* Original vs Updated comparison */}
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} sx={{ mt: 1 }}>
            {/* Original */}
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                bgcolor: 'error.50',
                borderColor: 'error.light',
              }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                color="error.main"
                display="block"
                mb={0.5}
              >
                ORIGINAL
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {change.original.sessionType}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDuration(change.original.durationMin)} •{' '}
                {formatIntensity(change.original.intensity)} • TSS: {change.original.predictedTSS}
              </Typography>
            </Paper>

            {/* Updated */}
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                bgcolor: 'success.50',
                borderColor: 'success.light',
              }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                color="success.main"
                display="block"
                mb={0.5}
              >
                UPDATED
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {change.updated.sessionType}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDuration(change.updated.durationMin)} •{' '}
                {formatIntensity(change.updated.intensity)} • TSS: {change.updated.predictedTSS}
              </Typography>
            </Paper>
          </Box>

          {/* Reason */}
          <Box mt={1.5}>
            <Typography variant="caption" color="text.secondary">
              Reason: {change.reason}
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

const AICoachModal: React.FC<AICoachModalProps> = ({
  isOpen,
  onClose,
  onChangesApplied,
  initialPrompt,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showSuccess, showApiError } = useNotification();

  // State
  const [prompt, setPrompt] = useState('');
  const [lastSentPrompt, setLastSentPrompt] = useState(''); // Track the original prompt for revisions
  const [isLoading, setIsLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [response, setResponse] = useState<AgentResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedChanges, setExpandedChanges] = useState<Set<number>>(new Set());
  const [isMockResponse, setIsMockResponse] = useState(false);
  const [hasAutoSent, setHasAutoSent] = useState(false);

  // Send prompt to AI Coach (extracted to reuse)
  const sendPrompt = React.useCallback(
    async (promptText: string) => {
      if (!promptText.trim()) return;

      setLastSentPrompt(promptText);
      setIsLoading(true);
      setError(null);
      setResponse(null);

      try {
        logger.info('Sending prompt to AI Coach', { prompt: promptText.slice(0, 100) });

        const result = await apiClient.sendAICoachMessage(promptText);

        if (result.success && result.data?.response) {
          setResponse(result.data.response);
          setIsMockResponse(result.data.mock === true);
          logger.info('AI Coach response received', {
            changeCount: result.data.response.changes?.length || 0,
            mock: result.data.mock,
          });
        } else {
          setError(result.error || result.message || 'Failed to get response from AI Coach');
          showApiError(result, 'AI Coach Error');
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        logger.error('AI Coach error', err as Error);
      } finally {
        setIsLoading(false);
      }
    },
    [showApiError]
  );

  // Auto-send initial prompt when modal opens with one
  React.useEffect(() => {
    if (isOpen && initialPrompt && !hasAutoSent) {
      setPrompt(initialPrompt);
      setHasAutoSent(true);
      // Send automatically after a brief delay to show the prompt
      const timer = setTimeout(() => {
        sendPrompt(initialPrompt);
      }, 500);
      return () => clearTimeout(timer);
    }
    if (!isOpen) {
      setHasAutoSent(false);
    }
  }, [isOpen, initialPrompt, hasAutoSent, sendPrompt]);

  // Reset state when modal closes
  const handleClose = () => {
    setPrompt('');
    setLastSentPrompt('');
    setResponse(null);
    setError(null);
    setExpandedChanges(new Set());
    setIsMockResponse(false);
    setHasAutoSent(false);
    onClose();
  };

  // Send prompt to AI Coach (button handler)
  const handleSend = async () => {
    await sendPrompt(prompt);
  };

  // Apply all changes
  const handleAcceptAll = async () => {
    if (!response?.changes?.length) return;

    setIsApplying(true);

    try {
      logger.info('Applying AI Coach changes', { changeCount: response.changes.length });

      const result = await apiClient.applyAICoachChanges(response.changes);

      logger.info('Apply changes result', {
        success: result.success,
        hasData: !!result.data,
        hasPlan: !!result.data?.plan,
        updatedCount: result.data?.updatedCount,
        planWorkouts: getAllWorkouts(result.data?.plan).length,
      });

      if (result.success && result.data?.plan) {
        // Build success message including sync status
        const changeMsg = `Applied ${result.data.updatedCount || response.changes.length} changes to your training plan`;
        const syncMsg = result.data.intervalsSync?.synced
          ? ` • ${result.data.intervalsSync.message}`
          : '';
        showSuccess(changeMsg + syncMsg);

        // Dispatch event to update the Dashboard with the new plan
        logger.info('Dispatching training-plan-updated event', {
          planWorkouts: getAllWorkouts(result.data.plan).length,
        });

        window.dispatchEvent(
          new CustomEvent('training-plan-updated', {
            detail: result.data.plan,
          })
        );

        onChangesApplied();
        handleClose();

        // Clear localStorage cache so reload fetches fresh from backend
        try {
          localStorage.removeItem('pacepilot_training_plan');
          localStorage.removeItem('pacepilot_plan_metadata');
          logger.info('Cleared localStorage cache');
        } catch (e) {
          logger.warn('Could not clear localStorage cache');
        }

        // Force page reload to ensure UI reflects changes
        logger.info('Reloading page to ensure UI updates');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else if (result.success && result.data?.updatedCount === 0) {
        // Changes were submitted but no matching dates found
        setError(
          'No matching workouts found for the proposed dates. The mock response may not match your actual training plan.'
        );
        showApiError('No workouts were updated', 'Apply Changes');
      } else {
        setError(result.error || result.message || 'Failed to apply changes');
        showApiError(result, 'Failed to Apply Changes');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      logger.error('Apply changes error', err as Error);
    } finally {
      setIsApplying(false);
    }
  };

  // Ask for revision - restore original prompt so user can modify it
  const handleAskRevision = () => {
    setPrompt(lastSentPrompt); // Restore the original prompt
    setResponse(null);
    setError(null);
    setExpandedChanges(new Set());
  };

  // Toggle change expansion
  const toggleChange = (index: number) => {
    setExpandedChanges(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // Handle Enter key in input
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}
    >
      {/* Title */}
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <AIIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              AI Training Coach
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" aria-label="Close AI Coach">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Input Section - show if no response yet */}
        {!response && (
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Tell me how you'd like to adjust your training plan. Click a suggestion or type your
              own:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {[
                "I can't train on Wednessday next week, can you move it?",
                'Make week 3 easier, I have a busy work week',
                "I've extra time this sunday, propose a harder session",
                'Swap my Tuesday and Wednesday workouts',
                "I'm feeling tired, reduce intensity this week",
              ].map(suggestion => (
                <Chip
                  key={suggestion}
                  label={suggestion}
                  onClick={() => setPrompt(suggestion)}
                  clickable
                  variant={prompt === suggestion ? 'filled' : 'outlined'}
                  color={prompt === suggestion ? 'primary' : 'default'}
                  size="small"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'primary.50',
                      borderColor: 'primary.main',
                    },
                  }}
                  aria-label={`Use suggestion: ${suggestion}`}
                />
              ))}
            </Box>

            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="What would you like to change about your training plan?"
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              aria-label="Training plan adjustment request"
              sx={{ mb: 2 }}
            />

            <Button
              variant="contained"
              onClick={handleSend}
              disabled={isLoading || !prompt.trim()}
              startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
              aria-label="Send request to AI Coach"
              fullWidth={isMobile}
              sx={{
                minWidth: 140,
                transition: 'all 0.3s ease',
              }}
            >
              {isLoading ? 'Thinking...' : 'Ask AI Coach'}
            </Button>
          </Box>
        )}

        {/* Loading State - Show below input while waiting */}
        {isLoading && (
          <Box py={4} display="flex" flexDirection="column" alignItems="center" gap={2}>
            <CircularProgress size={48} thickness={4} />
            <Typography variant="body1" color="text.secondary" textAlign="center">
              AI Coach is analyzing your training plan...
            </Typography>
            <Typography variant="caption" color="text.disabled">
              This may take a few seconds
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Response Section */}
        {response && !isLoading && (
          <Box>
            {/* Mock Response Indicator */}
            {isMockResponse && (
              <Alert severity="info" sx={{ mb: 2 }} icon={<AIIcon />}>
                <Typography variant="body2">
                  <strong>Demo Mode:</strong> This is a sample response. Configure the AI agent in
                  Azure to get personalized coaching advice.
                </Typography>
              </Alert>
            )}

            {/* Coach's Note */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                bgcolor: isMockResponse ? 'grey.100' : 'primary.50',
                borderColor: isMockResponse ? 'grey.300' : 'primary.light',
              }}
            >
              <Box display="flex" alignItems="flex-start" gap={1.5}>
                <AIIcon color={isMockResponse ? 'disabled' : 'primary'} sx={{ mt: 0.5 }} />
                <Box
                  sx={{
                    flex: 1,
                    '& p': { m: 0, mb: 1, '&:last-child': { mb: 0 } },
                    '& strong': { fontWeight: 600 },
                    '& ul, & ol': { m: 0, pl: 2.5, mb: 1 },
                    '& li': { mb: 0.5 },
                    '& h1, & h2, & h3': { mt: 1, mb: 0.5, fontWeight: 600 },
                    '& code': {
                      bgcolor: 'grey.100',
                      px: 0.5,
                      borderRadius: 0.5,
                      fontFamily: 'monospace',
                    },
                  }}
                >
                  <ReactMarkdown>{response.note}</ReactMarkdown>
                </Box>
              </Box>
            </Paper>

            {/* Changes Summary */}
            {response.changes && response.changes.length > 0 && (
              <>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" fontWeight={600}>
                    Proposed Changes ({response.summary.totalChanges})
                  </Typography>
                  <Chip
                    label={`TSS Impact: ${response.summary.tssImpact > 0 ? '+' : ''}${response.summary.tssImpact}`}
                    size="small"
                    color={
                      response.summary.tssImpact > 0
                        ? 'success'
                        : response.summary.tssImpact < 0
                          ? 'warning'
                          : 'default'
                    }
                  />
                </Box>

                <List disablePadding>
                  {response.changes.map((change, index) => (
                    <ChangeItem
                      key={`${change.date}-${index}`}
                      change={change}
                      expanded={expandedChanges.has(index)}
                      onToggle={() => toggleChange(index)}
                    />
                  ))}
                </List>
              </>
            )}

            {/* No Changes */}
            {(!response.changes || response.changes.length === 0) && (
              <Alert severity="info">The AI Coach didn't suggest any changes to your plan.</Alert>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Actions */}
      {response && !isLoading && (
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleAskRevision}
            startIcon={<RevisionIcon />}
            color="inherit"
            aria-label="Ask for revision"
          >
            Ask for Revision
          </Button>
          <Box flex={1} />
          <Button onClick={handleClose} color="inherit" aria-label="Decline changes">
            Decline
          </Button>
          <Button
            variant="contained"
            onClick={handleAcceptAll}
            disabled={isApplying || !response.changes?.length}
            startIcon={isApplying ? null : <AcceptIcon />}
            color="success"
            aria-label="Accept all changes"
          >
            {isApplying ? (
              <LoadingAnimation variant="dots" size="small" />
            ) : (
              `Accept All (${response.changes?.length || 0})`
            )}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AICoachModal;
